import React, { useContext } from 'react';
import { ThemeContext } from '../hooks/ThemeContext';
import { Link } from 'react-router-dom';
import { PenTool, Code, Palette, TrendingUp, Package, Shield } from 'lucide-react';

const Services = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  const services = [
    { 
      id: 'creations', 
      icon: <Palette className="w-8 h-8" />,
      title: 'Créations Digitales', 
      desc: 'Bannières, logos, illustrations et visuels pour réseaux sociaux. Créations professionnelles et sur-mesure.',
      features: ['Bannières personnalisées', 'Logos uniques', 'Templates réseaux sociaux', 'Illustrations digitales']
    },
    { 
      id: 'design', 
      icon: <PenTool className="w-8 h-8" />,
      title: 'Design Graphique', 
      desc: 'Identité visuelle complète, charte graphique et design personnalisé pour votre marque.',
      features: ['Identité visuelle', 'Charte graphique', 'Mockups produits', 'Packaging design']
    },
    { 
      id: 'web', 
      icon: <Code className="w-8 h-8" />,
      title: 'Développement Web', 
      desc: 'Sites web modernes, responsive et optimisés pour la performance et le SEO.',
      features: ['Sites vitrines', 'Sites e-commerce', 'Applications web', 'Optimisation SEO']
    },
    { 
      id: 'marketplace', 
      icon: <Package className="w-8 h-8" />,
      title: 'Marketplace Créative', 
      desc: 'Achetez et vendez des créations digitales. Plateforme sécurisée pour créateurs et clients.',
      features: ['Vente de créations', 'Protection acheteurs', 'Paiements sécurisés', 'Fichiers protégés']
    },
    { 
      id: 'community', 
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Communauté de Créateurs', 
      desc: 'Rejoignez notre réseau de créateurs talentueux et développez votre activité.',
      features: ['Inscription gratuite', 'Commission équitable', 'Support créateurs', 'Visibilité accrue']
    },
    { 
      id: 'quality', 
      icon: <Shield className="w-8 h-8" />,
      title: 'Qualité & Support', 
      desc: 'Service client réactif, qualité garantie et satisfaction assurée pour tous vos projets.',
      features: ['Support 24/7', 'Qualité garantie', 'Révisions incluses', 'Livraison rapide']
    },
  ];

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      {/* Hero Section */}
      <div className={`${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20' : 'bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="container mx-auto px-4 py-16 max-w-7xl text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Nos Services <span className="text-rose-500">Créatifs</span>
          </h1>
          <p className="text-lg md:text-xl opacity-90 max-w-3xl mx-auto mb-8">
            Une plateforme complète pour vos besoins créatifs. Achetez des créations uniques ou rejoignez notre communauté de créateurs talentueux.
          </p>
          <div className="flex flex-wrap gap-4 justify-center ">
            <Link 
              to="/shop" 
              className="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium"
            >
              Explorer la Boutique
            </Link>
            <Link 
              to="/devenir-createur" 
              className={`px-6 py-3 rounded-lg border transition-colors font-medium ${isDark ? 'border-white/20 hover:bg-white/10' : 'border-gray-300 hover:bg-white'}`}
            >
              Devenir Créateur
            </Link>
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="container mx-auto px-4 py-12 max-w-7xl ">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ">
          {services.map((s) => (
            <div 
              key={s.id} 
              className={`p-6 rounded-lg ${isDark ? 'bg-[#0b0b0c] border border-white/6 hover:border-white/12' : 'bg-white border border-gray-100 hover:shadow-lg'} transition-all`}
            >
              <div className={`inline-block p-3 rounded-lg mb-4 ${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20' : 'bg-rose-100 text-rose-600'}`}>
                {s.icon}
              </div>
              <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{s.desc}</p>
              <ul className="space-y-2 mb-4">
                {s.features.map((feature, idx) => (
                  <li key={idx} className={`text-sm flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="text-rose-500">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className={`mt-12 p-8 rounded-xl ${isDark ? 'bg-gradient-to-r from-rose-500/20 to-purple-500/20 border border-white/10' : 'bg-gradient-to-r from-rose-50 to-purple-50 border border-gray-200'}`}>
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">Prêt à démarrer ?</h2>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Que vous soyez créateur ou client, Krayne Agency vous accompagne dans tous vos projets créatifs.
            </p>
            <Link 
              to="/contact" 
              className="inline-block px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors font-medium"
            >
              Contactez-nous
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Services;
